/**
 * @file SiteTestimonialsPage.tsx
 * @description Контент → Свидетельства (phase19.8.5, переименовано в
 * phase19.10) — ручные витринные отзывы из БД (SiteReview). Рендерятся блоком
 * «Отзывы» конструктора с источником «База отзывов». Карточка: заголовок,
 * текст, автор, фото; переключатель активности и удаление.
 *
 * Это отдельная от клиентских отзывов сущность (R1 docs/business/reviews.md):
 * здесь полное редактирование разрешено, в рейтинге товара не участвует.
 * Клиентские отзывы модерируются на ProductReviewsPage.
 * Mobile-first: одна колонка карточек.
 */

import { useEffect, useState } from "react";

import { Field, ImageUrlField } from "../../../components/site-pages/fields";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { Switch } from "../../../components/ui/switch";
import { Textarea } from "../../../components/ui/textarea";
import { ApiError } from "../../../lib/api-client";
import { siteReviews, type SiteReview } from "../../../lib/api-site-pages";

export function SiteTestimonialsPage() {
  const notify = useNotify();
  const [items, setItems] = useState<SiteReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  async function refresh() {
    try {
      setItems(await siteReviews.list());
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate() {
    try {
      await siteReviews.create({
        title: "Новое свидетельство",
        body: "Текст свидетельства",
        author: "Автор",
      });
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось создать");
    }
  }

  async function onSave(review: SiteReview) {
    setSavingId(review.id);
    try {
      await siteReviews.update(review.id, {
        title: review.title,
        body: review.body,
        author: review.author,
        photoUrl: review.photoUrl ?? "",
        isActive: review.isActive,
      });
      notify.success("Свидетельство сохранено");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSavingId(null);
    }
  }

  async function onRemove(id: string) {
    if (!window.confirm("Удалить свидетельство?")) return;
    try {
      await siteReviews.remove(id);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить");
    }
  }

  function patch(id: string, patchValue: Partial<SiteReview>) {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patchValue } : r)));
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Свидетельства</h1>
          <p className="text-sm text-on-surface-muted">
            Ручные отзывы для блоков конструктора страниц. Клиентские отзывы о
            товарах — в разделе «Отзывы о товарах».
          </p>
        </div>
        <Button onClick={() => void onCreate()}>Добавить свидетельство</Button>
      </div>

      {loading ? (
        <p className="text-sm text-on-surface-muted">Загрузка…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-on-surface-muted">Свидетельств пока нет.</p>
      ) : (
        items.map((r) => (
          <div
            key={r.id}
            className="flex flex-col gap-3 rounded-lg border border-on-surface/10 bg-surface p-4"
          >
            <Field label="Заголовок">
              <Input
                value={r.title}
                onChange={(e) => patch(r.id, { title: e.target.value })}
              />
            </Field>
            <Field label="Текст свидетельства">
              <Textarea
                value={r.body}
                rows={4}
                onChange={(e) => patch(r.id, { body: e.target.value })}
              />
            </Field>
            <Field label="Автор">
              <Input
                value={r.author}
                onChange={(e) => patch(r.id, { author: e.target.value })}
              />
            </Field>
            <ImageUrlField
              label="Фото (опционально)"
              value={r.photoUrl ?? ""}
              onChange={(url) => patch(r.id, { photoUrl: url })}
            />
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={r.isActive}
                  onCheckedChange={(isActive) => patch(r.id, { isActive })}
                />
                Показывать на сайте
              </label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => void onRemove(r.id)}
                  className="text-rose-600"
                >
                  Удалить
                </Button>
                <Button disabled={savingId === r.id} onClick={() => void onSave(r)}>
                  {savingId === r.id ? "Сохранение…" : "Сохранить"}
                </Button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
